import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AppHttpService } from './app-http.service';
import { MapFeatureLinkCapture, MapFeatureLinkPin } from '../models/map-feature-link';

/** Every capture upload carries both: `design-context` like the snapshot, `map-capture` for the api's orphan sweep. */
export const CAPTURE_TAGS = 'design-context,map-capture';
export const CAPTURE_FILENAME = 'design-capture.png';

/**
 * The api's attachment row as `POST /v1/attachment` returns it (strings for coordinates). The
 * serializer sends the attachment's uuid as `id` and no `uuid` key; `uuid` is accepted too.
 */
export interface UploadedAttachmentRow {
  id?: string | null;
  uuid?: string | null;
  thumb_url?: string | null;
  preview_url?: string | null;
  original_url?: string | null;
  marked_up_url?: string | null;
  latitude?: string | number | null;
  longitude?: string | number | null;
}

/** A data URL → named File. multer reads the extension from the filename; a bare Blob uploads as "blob" with none. */
export function dataUrlToFile(dataUrl: string, name = CAPTURE_FILENAME): File {
  const [head, data = ''] = dataUrl.split(',');
  const type = /data:([^;]+)/.exec(head)?.[1] || 'image/png';
  const bytes = atob(data);
  const buffer = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) buffer[i] = bytes.charCodeAt(i);
  return new File([buffer], name, { type });
}

function numberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function attachmentUuid(row: UploadedAttachmentRow): string | null {
  return row?.uuid || row?.id || null;
}

/** The api row → the link's `capture` block (same shape `MapFeatureLinkService.serializeCapture` sends back). */
export function captureFromAttachment(row: UploadedAttachmentRow): MapFeatureLinkCapture {
  return {
    attachment_uuid: attachmentUuid(row),
    thumb_url: row.thumb_url || null,
    preview_url: row.marked_up_url || row.preview_url || row.original_url || null,
    original_url: row.original_url || null,
    has_markup: false,
    latitude: numberOrNull(row.latitude),
    longitude: numberOrNull(row.longitude),
  };
}

/**
 * Uploads and deletes the crew's map capture (spec 2026-09-27 §4). The capture is an ordinary
 * attachment until the record save attaches it through `map_feature_link.capture`.
 */
@Injectable({ providedIn: 'root' })
export class MapCaptureService {
  private http = inject(AppHttpService);

  async upload(dataUrl: string, pin: MapFeatureLinkPin): Promise<MapFeatureLinkCapture> {
    const body = {
      inputFiles: dataUrlToFile(dataUrl),
      'fileTags[0][tags]': CAPTURE_TAGS,
      'locations[0][latitude]': String(pin.latitude),
      'locations[0][longitude]': String(pin.longitude),
    };
    const response = await firstValueFrom(this.http.sendMultipartData('POST', '/v1/attachment', body)) as { data?: UploadedAttachmentRow[] };
    const row = response?.data?.[0];
    if (!row || !attachmentUuid(row)) throw new Error('The capture upload returned no attachment');
    return captureFromAttachment(row);
  }

  /** Orphan cleanup for a capture this dialog uploaded and will not attach (Retake, Remove, Cancel). */
  async delete(uuid: string): Promise<void> {
    await firstValueFrom(this.http.deleteService(`/v1/attachment/${encodeURIComponent(uuid)}`));
  }
}
